// Payment types and interfaces for PaymentsTab and CustomTable

import React from "react";

type PaymentStatus =
  | "succeeded"
  | "failed"
  | "refunded"
  | "voided"
  | "pending"
  | "partial refund"
  | "disputed"
  | "disputed lost"
  | "disputed won";

interface PaymentFilterTypes {
  offset: number;
  limit: number;
  method?: string;
  status?: string;
  amount?: string;
  startDate?: string;
  endDate?: string;
}

interface PaymentColumn {
  id: string;
  name: string;
  minWidth?: string;
  right?: string;
  cell: (row: PaymentRow) => JSX.Element;
}

interface PaymentsTabProps {
  failed?: boolean;
}

interface CustomTableProps {
  columns: PaymentColumn[];
  data: PaymentRow[];
  pagination?: boolean;
  fixedHeaderScrollHeight?: string;
  perPage: number;
  currentPage: number;
  totalCount: number;
  onPreviousClick?: (e?: any) => void;
  onNextClick?: (e?: any) => void;
  isTableLoading?: boolean;
  GetRow?: (row: PaymentRow) => JSX.Element;
  onRowClick?: (row: PaymentRow, e: any) => void;
}

export interface PaymentCountsResponseKeys {
  Succeeded: number;
  Failed: number;
  Other: number;
  Pending: number;
  PartiallyRefunded: number;
  Refunded: number;
  Disputed: number;
  DisputedWon: number;
  DisputedLoss: number;
  Voided: number;
}

export interface PaymentCountsResponse {
  data: PaymentCountsResponseKeys;
}

export interface ConvFeeResponse {
  body?: {
    success?: boolean;
    data?: {
      fee?: number | string | null;
    };
  };
}

export interface PaymentHistoryCount {
  Succeeded: number;
  Failed: number;
  // Other: number;
  // Pending: number;
  // PartiallyRefunded: number;
  // Refunded: number;
  // Disputed: number;
  // DisputedWon: number;
  // DisputedLoss: number;
  // Voided: number;
  all: number;
  refundCount: number;
}

export interface PaymentRow {
  id: number;
  card_last_4_digits: string;
  accounts_id: number;
  first_name: string;
  last_name: string;
  amount: number;
  payment_method: string;
  card_type: string;
  insert_date: string;
  /** Latest 'payment_recovered' timeline date; null unless recovered_payment === 'Y'. */
  recovered_date?: string | null;
  /** Date to display: recovered_date when present, otherwise insert_date. */
  payment_date?: string;
  last_update: string;
  transaction_id: string;
  transaction_status: string;
  payment_gateway_response: string;
  ivr_type: string;
  customerid: number;
  retry_count: number;
  recovered_payment: string;
  lead_owner: string;
  clusterid: number;
  cc_ref_number: string;
  cc_fee_amount: number;
  ach_fee_amount: number;
  description: string;
  account_number: string;
  bank_name: string;
  customerData: CustomerData | null;
  payment_status: string;
  receiptId?: string;
  payment_note?: string;
}
interface CustomerData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  city: string;
  state: string;
  zip: string;
  address1: string;
  address2: string;
  billing_first_name: string;
  billing_last_name: string;
  billing_city: string;
  billing_state: string;
  billing_zip: string;
  billing_address1: string;
  billing_address2: string;
  next_billing_date: string;
  payment_schedule: string;
  amount: number;
  remaining_payments: number;
  total_payments: number;
  last_payment_date?: null;
  currunt_billing_count: number;
  creation_date: string;
  updation_date: string;
  clusterid: number;
  cc_ref_number: string;
  cc_expiry: string;
  cc_cvv: string;
  cc_name: string;
  cc_type: string;
  cc_4digit: string;
  lead_id: number;
  end_date?: null;
  payment_attempts: number;
  description: string;
  last_payment_status: string;
  refnum: string;
  gateway_id: number;
  initial_balance: number;
  exclude_holidays: string;
  incremented_days: number;
  cc_fee_type: string;
  ach_fee_type: string;
  cc_fee_amount: number;
  ach_fee_amount: number;
  plan_id: number;
  payment_method: string;
  bank_name: string;
  bank_address: string;
  routing_number: string;
  account_number: string;
  settings_payment_method: string;
  plaid_access_token: string;
  cpp_id: string;
  vdc_id: number;
  offer_type: string;
  remaining_balance: number;
  agreement_id?: null;
  agent: string;
  transaction_type: string;
  lead_owner: string;
  payment_source: string;
  paypal_token?: null;
  next_failed_payment_date?: null;
  custom_schedule: string;
  show_series: string;
  temp_field?: null;
  payment_method_updated: string;
  accounts_id: number;
  failed_attempts: number;
  cancelling_reason?: null;
  schedule_tz: string;
  statement_descriptor?: null;
  currency: string;
  starts_when: string;
  start_sub?: null;
  ends_when: string;
  end_sub?: null;
  cycles?: null;
  notes?: null;
  allowed_payment_methods?: null;
  payment_collection_type?: null;
  memo_text?: null;
  contact_method: string;
  cancel_at_period_end?: null;
  canceled_at?: null;
  days_until_due?: null;
  ended_at?: null;
  last_invoice?: null;
  pending_setup_intent?: null;
  quantity?: null;
  sub_status?: null;
  subscription_paused: string;
  subs_paused_date?: null;
  subs_resume_date?: null;
  subscription_id: string;
  recurring_type: string;
  proration: string;
  product_link_id: number;
}

export type PaymentColumns = {
  id: string;
  name: string;
  minWidth: string;
  right?: string;
  cell: (row: PaymentRow) => React.ReactNode;
};

export interface PaymentMethodList {
  name: string;
  icon: React.ReactNode;
}

export interface FormRef {
  callHandleClick: () => void;
}

export enum PaymentGateway {
  STRIPE = "Stripe",
  STAX = "Stax",

  ACHQ = "ACHQ",


}

enum PaymentMethods {
  CARD = "Card",
  BANK = "Bank",
  PAYPAL = "PayPal",
  CASHAPPPAY = "Cash App Pay",
  GOOGLEPAY = "Google Pay",
  APPLEPAY = "Apple Pay",
  WIRE = "Wire",
  AMAZONPAY = "Amazon Pay",
}

export enum SettingsValue {
  YES = "Y",
  NO = "N",
  OFF = "O",
  CUSTOM = "C",
  DEFAULT = "D",
  OFF_STR = "OFF",
}

export enum OverPaymentProtectionEnum {

  ALERT = "AN",
  BLOCK = "BP",
}

export interface AddressPayload {
  line1?: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  state?: string;
}
